import { Component, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BoardService } from '../../../../../services/board.service';
import { StackedSidebarService } from '../../../../../services/stacked-sidebar.service';
import { DialogService } from '../../../../../services/dialog.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { SidebarType } from '../../../../layout/stacked-sidebar/stacked-sidebar.interface';
import { Call } from '../../../board-v3.types';

@Component({
  selector: 'app-board-v3-card-calls-panel',
  templateUrl: './card-calls-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardCallsPanelComponent implements OnChanges {
  @Input() cardId!: string;

  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  calls: Call[] = [];
  loading = false;
  loaded = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardId'] && this.cardId) {
      this.loaded = false;
      this.calls = [];
      this.loadCalls();
    }
  }

  loadCalls(): void {
    if (!this.cardId || this.loading) return;

    this.loading = true;
    this.boardService
      .getCalls(this.cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (calls) => {
          this.calls = calls || [];
          this.loaded = true;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loaded = true;
        },
      });
  }

  formatDuration(durationMs: number): string {
    const totalSeconds = Math.round((durationMs || 0) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  formatCallDate(call: Call): string {
    const raw = call.x_data?.calldate || call.created_at;
    if (!raw) return '';

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';

    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  openTranscript(call: Call): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.BoardV3CallTranscript, width: '50vw' }, { callId: call.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  onDeleteClick(call: Call): void {
    this.dialogService
      .openConfirm({ text: 'board_v3_calls_delete_confirm' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.boardService
          .deleteCall(call.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.calls = this.calls.filter((item) => item.id !== call.id);
            },
            error: () => {
              this.notificationService.showError('', this.translateService.instant('board_v3_calls_delete_error'));
            },
          });
      });
  }
}
